import type { Answers, SummaryDashboard, TSuppressionProfile } from "./_schema";
import { deriveBoostProfile, type ExtraSignals } from "./boost-profile";
import { EN_PROFILE_COPY, type ProfileCopyPack } from "./profile-copy";

/**
 * CORTISOL WEIGHT-LOSS deriver. Wraps `deriveBoostProfile` with the same
 * four moves every angle quiz makes (rule 11a) -- the base deriver is never
 * edited, and neither is `deriveCortisolProfile`, which is shared by the live
 * `cortisol` quiz and its four tracking clones.
 *
 *   1. Answer translation: `tension` -> quiz-v6's `stress-symptoms`, and
 *      `fat-location` -> quiz-v6's `body-composition`. The second is the
 *      important one: this quiz replaced cortisol's body-composition screen
 *      with a better question for the angle, and without the translation that
 *      input would simply vanish from the verdict.
 *   2. Sign severity through the `extraSignals` seam only (severity, never
 *      evidence or root causes). Worst answer -> tier1, second-worst -> tier2,
 *      matching the quiz-v7 cohort-gradient convention.
 *   3. Forced primary: `cortisol-dominance` leads on every path, because that
 *      is the entry promise of the ad, the intro and both bridges.
 *   4. Cohort: `cortisol-weight`, so the result/projection/offer surfaces
 *      speak about storage rather than "energy, strength and masculine drive".
 *
 * Never map one answer into BOTH a translation and a tier map -- that
 * double-counts the same signal.
 *
 * On top of those four moves, the deriver also carries a fifth, narrower one:
 * a ZERO-SIGNS EXEMPTION to the rule-12a severity floor (see below), the same
 * exception the operator ruled for `cortisol-story` on 2026-09-02. That
 * exemption stamps `cortisol-weight-clear` instead of `cortisol-weight` (move
 * 4 above) -- the normal cohort's endgame promises to fix a belly and
 * cravings this man denied having, so the zero-signs path needs its own,
 * opposite-premise cohort rather than reusing the symptomatic one.
 */

/** `tension` is cortisol's phrasing of quiz-v6's `stress-symptoms`. */
const TENSION_TO_STRESS: Record<string, string> = {
  constantly: "yes",
  "most-days": "yes",
  occasionally: "no",
  rarely: "no",
};

/**
 * `fat-location` is this quiz's replacement for cortisol's
 * `body-composition` screen. Central and chest storage read as the softer
 * quiz-v6 body types; "not sure" lands mid-scale rather than flattering him.
 */
const FAT_TO_BODY_COMPOSITION: Record<string, string> = {
  belly: "overweight",
  chest: "overweight",
  handles: "soft-some-muscle",
  everywhere: "average",
};

/** Ids whose values are ignite-verbatim -- copied through untranslated. */
const PASSTHROUGH_IDS = ["age", "energy-3pm", "when-started"] as const;

const TIER1_SIGNS: Record<string, readonly string[]> = {
  "puffy-face": ["yes-noticeably"],
  "wake-pattern": ["most-nights"],
  libido: ["significantly"],
  tried: ["clean-still"],
};
const TIER2_SIGNS: Record<string, readonly string[]> = {
  "puffy-face": ["some-mornings"],
  "wake-pattern": ["few-times-week"],
  libido: ["somewhat"],
  tried: ["effort-gap"],
  "sugar-cravings": ["daily"],
};
const TIER3_SIGNS: Record<string, readonly string[]> = {
  "sugar-cravings": ["few-times-week"],
};

function countTier(answers: Answers, map: Record<string, readonly string[]>): number {
  let n = 0;
  for (const [id, values] of Object.entries(map)) {
    const v = answers[id];
    if (typeof v === "string" && values.includes(v)) n += 1;
  }
  return n;
}

function signSignals(answers: Answers): ExtraSignals {
  return {
    tier1: countTier(answers, TIER1_SIGNS),
    tier2: countTier(answers, TIER2_SIGNS),
    tier3: countTier(answers, TIER3_SIGNS),
  };
}

function syntheticAnswers(answers: Answers): Answers {
  const out: Answers = {};
  for (const id of PASSTHROUGH_IDS) {
    const v = answers[id];
    if (typeof v === "string") out[id] = v;
  }
  const tension = answers["tension"];
  if (typeof tension === "string" && TENSION_TO_STRESS[tension]) {
    out["stress-symptoms"] = TENSION_TO_STRESS[tension];
  }
  const fat = answers["fat-location"];
  if (typeof fat === "string" && FAT_TO_BODY_COMPOSITION[fat]) {
    out["body-composition"] = FAT_TO_BODY_COMPOSITION[fat];
  }
  return out;
}

/**
 * The eight signs, in screen order. This is the AD PANEL, not the severity
 * tiers above: the tier maps cover only the questions that feed severity
 * through `extraSignals`, while the ad promises eight and the reader is
 * invited to count all eight. Deriving the tally from the tier maps would
 * under-report.
 *
 * `id` is the question id; `key` is the tally row key (rule 22 charset).
 */
export const SIGN_ROWS = [
  { key: "belly-fat", id: "fat-location", label: "Belly fat" },
  { key: "effort-gap", id: "tried", label: "Effort gap" },
  { key: "cravings", id: "sugar-cravings", label: "Cravings" },
  { key: "puffy-face", id: "puffy-face", label: "Puffy face" },
  { key: "wake-2-3am", id: "wake-pattern", label: "2-3am wake" },
  { key: "crash-3pm", id: "energy-3pm", label: "3pm crash" },
  { key: "low-drive", id: "libido", label: "Low drive" },
  { key: "tension", id: "tension", label: "Tension" },
] as const;

/** The answers that count as "reported", per sign question id. */
const REPORTED_SIGNS: Readonly<Record<string, readonly string[]>> = {
  "fat-location": ["belly", "chest"],
  tried: ["clean-still", "effort-gap"],
  "sugar-cravings": ["daily", "few-times-week"],
  "puffy-face": ["yes-noticeably", "some-mornings"],
  "wake-pattern": ["most-nights", "few-times-week"],
  "energy-3pm": ["drained", "afternoon-slump"],
  libido: ["significantly", "somewhat"],
  tension: ["constantly", "most-days"],
};

export function isReportedSign(answers: Answers, id: string): boolean {
  const values = REPORTED_SIGNS[id];
  const v = answers[id];
  return values !== undefined && typeof v === "string" && values.includes(v);
}

/**
 * How many of the eight the user has affirmed SO FAR. Pure function of
 * answers-so-far (rule 11), so it is safe to call mid-flow: unanswered
 * questions simply do not count, which is exactly what the halfway tally
 * needs.
 */
export function countReportedSigns(answers: Answers): number {
  let n = 0;
  for (const id of Object.keys(REPORTED_SIGNS)) {
    if (isReportedSign(answers, id)) n += 1;
  }
  return n;
}

/**
 * Verdict copy for the optimization path.
 *
 * `label` / `headline` come from the copy pack's existing `low` entries
 * rather than being forked here, so a future locale clone inherits them from
 * its own pack. Only `explanation` and `expectedOutcome` are authored,
 * because the pack keys explanations by root cause and tone, neither of which
 * can express "this man has no symptoms" without adding a tone to every pack.
 *
 * Kept near the ~110-char house median: it renders inside the fold-critical
 * assessment card (rule 11a).
 */
const OPTIMIZATION_EXPLANATION =
  "You reported none of the eight signs. There is nothing here to reverse, so the work is raising your ceiling.";

const OPTIMIZATION_OUTCOME = {
  label: "Performance Headroom",
  copy: "Your protocol lowers the cortisol ceiling that decides how much of your testosterone you actually get to use.",
} as const;

/**
 * ZERO-SIGNS EXEMPTION to the rule-12a severity floor.
 *
 * The floor exists because a completer of a symptom-motivated quiz is feeling
 * a consequence, so absolving him is a lie. That premise is FALSE BY
 * CONSTRUCTION for a man who has answered "no" to all eight signs -- and this
 * quiz makes him count them out loud twice (the halfway tally, and the
 * reactive line on the mechanism bridge), so he knows exactly what he
 * reported and reads "EARLY DECLINE" as the quiz being rigged.
 *
 * Operator directive 2026-09-02, given for `cortisol-story` and carried over
 * here because this quiz has the same count-out-loud property: tell him he is
 * fine, then sell the next level rather than a repair.
 *
 * `rootCauses` pass through untouched, so the forced `cortisol-dominance`
 * primary still leads on every path. Only the fields a clean man would read
 * as false are rewritten -- the original four verdict fields, plus `cohort`
 * (see `cortisol-weight-clear` in quizzes/quiz-v7.ts's `COHORT_OVERLAYS`,
 * added the same session this comment was written, so the endgame stops
 * promising to fix a belly and cravings he just denied having). The
 * projection chart already renders `low` as "BASELINE OPTIMIZATION", which
 * frames headroom rather than absolving symptoms.
 */
export function deriveCortisolWeightlossProfile(
  answers: Answers,
  opts: { copy?: ProfileCopyPack } = {},
): TSuppressionProfile {
  const profile = deriveBoostProfile(syntheticAnswers(answers), {
    extraSignals: signSignals(answers),
    forcePrimaryCause: "cortisol-dominance",
  });
  const base: TSuppressionProfile = { ...profile, cohort: "cortisol-weight" };
  if (countReportedSigns(answers) > 0) return base;

  const copy = opts.copy ?? EN_PROFILE_COPY;
  return {
    ...base,
    // The endgame is the fifth move this exemption owns: `cortisol-weight`'s
    // COHORT_OVERLAYS entry promises to fix a belly and cravings this man just
    // denied having, so the zero-signs path stamps a second, opposite-premise
    // cohort (`cortisol-weight-clear`) instead. Operator ruling, this session.
    cohort: "cortisol-weight-clear",
    suppressionLevel: "low",
    declinePattern: "stable",
    assessment: {
      label: copy.assessmentLabel.low,
      headline: copy.assessmentHeadline.low,
      explanation: OPTIMIZATION_EXPLANATION,
    },
    expectedOutcome: { ...OPTIMIZATION_OUTCOME },
  };
}

/**
 * The eight signs, counted out loud. Serves BOTH tally screens -- `signs-so-far`
 * at the halfway point and `signs-final` after the last question.
 *
 * Pure function of answers-so-far (rule 11). A sign the quiz has not asked
 * about yet is `unasked`, never `absent`: rendering it absent would tell the
 * reader he does not have something he was never asked, on a screen whose
 * entire credibility rests on only saying back what he said.
 *
 * That state is read from whether the ANSWER IS PRESENT, not from a list of
 * which signs come before the halfway screen. It was such a list
 * (`HALF_POOL_IDS`, removed 2026-09-10) and that was correct for exactly one
 * screen: the closing tally would have rendered "not asked yet" beside the
 * four signs the reader had just answered, on the screen whose whole job is to
 * show him his completed count. The presence check leaves the halfway screen's
 * output byte-identical -- those four ids are genuinely unanswered there -- and
 * makes the same function right on both.
 */
export function deriveCortisolWeightlossSummary(answers: Answers): SummaryDashboard {
  const rows = SIGN_ROWS.map((sign) => ({
    key: sign.key,
    label: sign.label,
    state: (typeof answers[sign.id] !== "string"
      ? "unasked"
      : isReportedSign(answers, sign.id)
        ? "confirmed"
        : "absent") as "confirmed" | "absent" | "unasked",
  }));
  return {
    tally: {
      confirmed: rows.filter((r) => r.state === "confirmed").length,
      total: rows.length,
      rows,
    },
    footnote: "Self-assessment. Not a lab result.",
  };
}

/**
 * BRIDGE 1 -- absolution. Takes the blame off him before the quiz asks what
 * the pattern has cost.
 *
 * It deliberately does NOT name the mechanism. The operator's source doc
 * explained here that high cortisol suppresses testosterone, which is bridge
 * 2's entire payload five questions later; saying it twice spends the reveal
 * early. Live `cortisol` splits the two the same way.
 *
 * Three buckets (zero / one / many) per .claude/screen-schema.md. Only the
 * `many` bucket carries the number; zero and one state their count in words,
 * so a rendered digit can never sit beside a mismatched noun.
 */
export function buildAbsolutionBody(answers: Answers): string[] {
  return [...ABSOLUTION_OPENING, absolutionClose(countReportedSigns(answers)).paragraph];
}

/**
 * The span of bridge 1 that renders in mars-orange.
 *
 * It is the reactive clause and nothing else: the two opening paragraphs read
 * the same for every man, so highlighting them would say "this line is about
 * you" about copy that is about everyone.
 *
 * Returned as a substring rather than declared in the screen's `bodyAccents`
 * because the many-bucket sentence contains a digit and so appears in no
 * static paragraph -- `bodyAccents` is validated against the static `body` at
 * parse time and could not hold it. See the `RecapSegment` docstring in
 * quizzes/_schema.ts for why that carve-out is narrow, and
 * tests/quizzes/cortisol-weightloss-summary.test.ts for the tests that replace
 * the parse-time check it gives up.
 *
 * Each span is ~30 characters, well inside the phrase-length budget in
 * .claude/screen-schema.md (a 70-char accent renders as three full lines of
 * orange, which is a highlighter rather than a scan layer).
 */
export function buildAbsolutionAccent(answers: Answers): string {
  return absolutionClose(countReportedSigns(answers)).accent;
}

/** Reads the same for every man, so deliberately not accented. */
const ABSOLUTION_OPENING = [
  "You go to the gym, you eat clean, and the mirror still doesn't match the effort.",
  "That's not laziness, and it's not just getting older. Modern life puts your body under a constant, low-grade pressure it was never built for -- the kind that quietly overrides discipline and keeps the fat locked in place.",
];

/**
 * Bridge 1's reactive close: the paragraph, and the leading span of it that
 * renders orange.
 *
 * One function returns both so the accent is a prefix of its own paragraph BY
 * CONSTRUCTION. Split across two functions, an edit to one could leave the
 * accent matching nothing -- which renders the paragraph plain white and fails
 * nothing at all.
 */
function absolutionClose(n: number): { paragraph: string; accent: string } {
  if (n === 0) {
    const accent = "So far it isn't showing up";
    return {
      paragraph: `${accent} in the places it usually shows up first. That's worth knowing too. A few more questions and we'll tell you what it means.`,
      accent,
    };
  }
  const accent =
    n === 1 ? "You've already named one of them." : `You've already named ${n} of them.`;
  return { paragraph: `${accent} Something else is running the show.`, accent };
}

/**
 * BRIDGE 2 -- name the villain. The full mechanism lands here and nowhere
 * earlier.
 *
 * The zero bucket is a different argument, not a softened one: "these signs
 * point to one thing" refers to nothing when he reported nothing, so that
 * reader gets a headroom framing that sets up the optimization verdict.
 */
export function buildMechanismBody(answers: Answers): string[] {
  const n = countReportedSigns(answers);
  if (n === 0) {
    return [
      mechanismCount(0),
      "Cortisol is your body's alarm system, built for short bursts. For most men it never gets to switch off, and stuck high it tells the body to store belly fat and pull back on the testosterone that would otherwise burn it off. That isn't where you are.",
      "What you have is room, and most men never find that out until it's gone. Let's see how much headroom you've actually got.",
    ];
  }
  return [
    mechanismCount(n),
    "Cortisol is your body's alarm system, built for short bursts. A threat appears, it spikes, the threat passes, it drops. Modern life never lets it drop. Stuck high, it tells your body to store belly fat, hold water in your face, and pull back on the testosterone that would otherwise burn it off.",
    "That's why the diet and the gym haven't been enough. You've been fighting the fat without touching the signal that puts it there.",
    "Lower the cortisol, free the testosterone. Let's see how high yours is running.",
  ];
}

/**
 * BRIDGE 2's count line -- its first paragraph, and the span that renders in
 * mars-orange.
 *
 * The whole paragraph is the accent here, unlike bridge 1 where the reactive
 * clause is only the tail of a longer sentence: this one IS a standalone
 * 30-character line, so highlighting part of it would split a sentence that
 * already reads as a single stat. Same construction rule as bridge 1 -- one
 * function builds the line and both the body and the accent read it, so the
 * two cannot drift into a silent no-op.
 *
 * The reactive HEADING stays white. Bridge 1 established that orange means
 * "this line is about you" on the body copy; turning a full Martian Mono h2
 * orange as well would make the screen two competing headlines, and the
 * heading is already the visual anchor.
 */
function mechanismCount(n: number): string {
  if (n === 0) return "You reported none of the eight.";
  return n === 1 ? "You reported one of the eight." : `You reported ${n} of the eight.`;
}

export function buildMechanismAccent(answers: Answers): string {
  return mechanismCount(countReportedSigns(answers));
}

/**
 * BRIDGE 2's heading. Reactive for the same reason its body is: at a count of
 * zero, "these signs point to one thing" refers to nothing, and the reader has
 * just counted the signs out loud twice so he knows it. The schema's own
 * `deriveBody` docstring forbids a static fallback the derived copy
 * contradicts, and a heading is no different.
 */
export function buildMechanismHeading(answers: Answers): string {
  return countReportedSigns(answers) === 0
    ? "You're not carrying the pattern most men your age are carrying."
    : "These signs point to one thing: cortisol is running the show, and your belly is the symptom.";
}
